// ─────────────────────────────────────────────────────────────────────────────
// 🚨 THE CLIENT'S COPY OF THE AUDIENCE CUTOFF, SO THE APP DOES NOT HAVE TO BE
// TOLD BY A 401.
//
// backend/constants/tokenAudience.js refuses a token with no `aud` claim from
// 2026-11-06. Until this file existed, the browser and the native app found that
// out the hard way: AuthContext's startup check validated `exp` ONLY, so a
// borrower holding a pre-audience token booted into the authenticated app as
// though nothing were wrong, fired its first request, and got a 401. The
// interceptor then cleared the session and redirected.
//
// 🔑 IT ENDED UP AT /login EITHER WAY — the loop was never the risk. The risk is
// everything that renders in between: a dashboard mounting four fetches, four
// rejections, and whatever error state each of those components paints, for the
// fraction of a second before window.location.href replaces the page. On a cold
// native start that is a visible flash of broken app, and it is the first thing
// a borrower would see after the cutoff.
//
// ⚠️ A MIRROR, AND IT CANNOT IMPORT THE ORIGINAL. The frontend does not build
// from backend/. test/tokenAudienceParity.test.js reads both files and fails if
// the date or the rule drifts — the same arrangement staffRoleAccess.js uses.
//
// ⚠️ THIS IS NOT A SECURITY CONTROL AND MUST NEVER BE TREATED AS ONE. The server
// decides; nothing here can grant access. All this does is let the client reach
// the same conclusion a moment earlier, so it shows a login screen instead of a
// broken dashboard.
// ─────────────────────────────────────────────────────────────────────────────

/** Mirror of backend GRACE_UNTIL — the instant an audience-less token dies. */
export const GRACE_UNTIL = Date.parse('2026-11-06T00:00:00Z');

/** Mirror of backend AUD. The borrower apps only ever hold 'borrower'. */
export const AUD = Object.freeze({ BORROWER: 'borrower', STAFF: 'staff', SERVICE: 'service' });

/**
 * Would the server refuse this payload's audience right now?
 *
 * @param {object} payload   decoded (NOT verified) JWT payload
 * @param {string} expected  the audience this app holds
 * @returns {boolean} true when the server would refuse it
 *
 * ⚠️ MISSING AND WRONG ARE DIFFERENT, exactly as on the server. A missing
 * audience is tolerated until the cutoff; a wrong one never is — a staff token
 * in the borrower app is not an old token, it is the wrong token, and waiting
 * for a cutoff to act on it would be incoherent.
 */
export function audienceRejected(payload, expected, now = Date.now()) {
  if (!payload) return false;            // not our business — exp/parse handle it
  const aud = payload.aud;
  if (aud === undefined || aud === null || aud === '') return now >= GRACE_UNTIL;
  return aud !== expected;
}
